import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  DocumentTextIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
  ClipboardListIcon,
  CogIcon,
  ArrowLeftIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import {
  getPlanByIdThunk,
  getPlanLedgerThunk,
} from "../../../../store/thunks/jewelleryPlan/planThunks";
import PlanSummary from "./PlanSummary";
import PaymentHistory from "./PaymentHistory";
import InstallmentSchedule from "./InstallmentSchedule";
import LedgerView from "./LedgerView";
import PlanActions from "./PlanActions";
import { JEWELLERY_ROUTES } from "constants/jewellery.constant";
import PauseHistoryTab from "./PauseHistoryTab";

const PlanDetailsPage = () => {
  const { planId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const { selectedPlan, planSchedule, planLedger, loading, error } =
    useSelector((state) => state.jewelleryPlan?.plans);

  useEffect(() => {
    if (planId) {
      dispatch(getPlanByIdThunk(planId));
    }
  }, [dispatch, planId]);

  useEffect(() => {
    if (
      (activeTab === "ledger" || activeTab === "payments") &&
      selectedPlan &&
      planLedger.length === 0
    ) {
      dispatch(getPlanLedgerThunk(planId));
    }
  }, [activeTab, selectedPlan, planLedger.length, dispatch, planId]);

  const tabs = [
    {
      id: "overview",
      name: "Overview",
      icon: DocumentTextIcon,
      component: PlanSummary,
    },
    {
      id: "payments",
      name: "Payments",
      icon: CurrencyRupeeIcon,
      component: PaymentHistory,
    },
    {
      id: "schedule",
      name: "Schedule",
      icon: CalendarIcon,
      component: InstallmentSchedule,
    },
    {
      id: "pause-history",
      name: "Pause History",
      icon: ClockIcon,
      component: PauseHistoryTab,
    },
    {
      id: "ledger",
      name: "Ledger",
      icon: ClipboardListIcon,
      component: LedgerView,
    },
    {
      id: "actions",
      name: "Actions",
      icon: CogIcon,
      component: PlanActions,
    },
  ];

  if (loading && !selectedPlan) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-danger-light border border-danger rounded-lg p-4">
          <p className="text-danger">Error loading plan: {error}</p>
          <button
            onClick={() => navigate(JEWELLERY_ROUTES.ACTIVE_PLANS)}
            className="mt-2 text-sm text-danger hover:text-danger-dark"
          >
            Back to Plans
          </button>
        </div>
      </div>
    );
  }

  if (!selectedPlan) return null;

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-neutral-light"
            >
              <ArrowLeftIcon className="h-5 w-5 text-neutral-dark" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-text">
                {selectedPlan.planNumber}
              </h1>
              <p className="mt-1 text-sm text-neutral-dark">
                {selectedPlan.userId?.name} • {selectedPlan.userId?.phoneNumber}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium
              ${
                selectedPlan.status === "ACTIVE"
                  ? "bg-success-light text-success-dark"
                  : selectedPlan.status === "MATURED"
                    ? "bg-secondary-light text-secondary-dark"
                    : selectedPlan.status === "REDEEMED"
                      ? "bg-primary-light text-primary-dark"
                      : "bg-neutral-light text-neutral-dark"
              }`}
            >
              {selectedPlan.status}
            </span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-neutral-light rounded-lg p-3">
            <p className="text-xs text-neutral-dark">Monthly Amount</p>
            <p className="text-lg font-semibold text-text">
              ₹{selectedPlan.monthlyAmount?.toLocaleString()}
            </p>
          </div>
          <div className="bg-neutral-light rounded-lg p-3">
            <p className="text-xs text-neutral-dark">Payments Done</p>
            <p className="text-lg font-semibold text-text">
              {selectedPlan.paymentsDone || 0} /{" "}
              {selectedPlan.templateSnapshot?.tenureMonths || 0}
            </p>
          </div>
          <div className="bg-neutral-light rounded-lg p-3">
            <p className="text-xs text-neutral-dark">Total Paid</p>
            <p className="text-lg font-semibold text-text">
              ₹{(selectedPlan.totalPaidAmount || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-neutral-light rounded-lg p-3">
            <p className="text-xs text-neutral-dark">Wallet Balance</p>
            <p className="text-lg font-semibold text-text">
              {selectedPlan.templateSnapshot?.type === "WEIGHT_SAVINGS"
                ? `${selectedPlan.wallet?.grams || 0} gms`
                : `₹${(selectedPlan.wallet?.valueInr || 0).toLocaleString()}`}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-background shadow rounded-lg">
        <div className="border-b border-neutral-light">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-neutral-dark hover:text-text hover:border-neutral-light"
                  }`}
              >
                <tab.icon
                  className={`-ml-0.5 mr-2 h-5 w-5
                    ${
                      activeTab === tab.id
                        ? "text-primary"
                        : "text-neutral-dark group-hover:text-text"
                    }`}
                />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {ActiveComponent && (
            <ActiveComponent
              plan={selectedPlan}
              schedule={planSchedule}
              ledger={planLedger}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanDetailsPage;
